import React from 'react';
import { ChevronLeft, ChevronRight, ChevronsLeft, ChevronsRight } from 'lucide-react';
import { useTranslation } from 'react-i18next';

interface TableNavigationProps {
  pageIndex: number;
  pageSize: number;
  totalRows: number;
  totalPages: number;
  pageSizeOptions?: number[];
  onPageChange: (newIndex: number) => void;
  onPageSizeChange: (newSize: number) => void;
  /**
   * Position of this navigation bar relative to the table.
   * - 'top':    rendered above the table (border-t + border-b)
   * - 'bottom': rendered below the table (border-t)
   */
  position?: 'top' | 'bottom';
  /** DOM id — permite apuntar a esta barra por CSS/tests (convención: pag-<seccion>-<tab>). */
  id?: string;
  /** Clases extra para el contenedor raíz. */
  className?: string;
}

/**
 * Reusable 3-column navigation bar for paginated tables.
 *
 * Layout:
 *   - Left:   "Showing X-Y of Z (Page A of B)"
 *   - Center: Rows-per-page dropdown
 *   - Right:  Prev/Next + page indicator
 *
 * Designed to be rendered both above and below the table — a UX best
 * practice for long tables so users don't need to scroll to find paging
 * controls. Uses tabular-nums for stable digit alignment.
 */
const TableNavigation: React.FC<TableNavigationProps> = ({
  pageIndex,
  pageSize,
  totalRows,
  totalPages,
  pageSizeOptions = [50, 100, 200, 500, 1000],
  onPageChange,
  onPageSizeChange,
  position = 'top',
  id,
  className = '',
}) => {
  const { t } = useTranslation();

  const start = totalRows === 0 ? 0 : pageIndex * pageSize + 1;
  const end = Math.min((pageIndex + 1) * pageSize, totalRows);
  const currentPage = totalPages === 0 ? 0 : pageIndex + 1;

  const borderClasses =
    position === 'top'
      ? 'border-t border-b border-gray-200'
      : 'border-t border-b border-gray-200';

  return (
    <div
      id={id}
      data-role="listing-pagination"
      className={`flex flex-col sm:flex-row items-stretch sm:items-center justify-between gap-2 px-3 py-2 bg-gray-50 ${borderClasses} ${className}`}
    >
      {/* Left: showing X-Y of Z */}
      <div className="text-xs text-gray-600 tabular-nums sm:flex-1">
        <span className="font-bold text-gray-800">
          {t('table.showing')} {start.toLocaleString('es-AR')}-{end.toLocaleString('es-AR')} {t('table.of')} {totalRows.toLocaleString('es-AR')}
        </span>
        {totalPages > 0 && (
          <span className="text-gray-400 ml-2">
            ({t('table.page')} {currentPage} {t('table.of')} {totalPages})
          </span>
        )}
      </div>

      {/* Center: rows per page */}
      <div className="flex items-center justify-center gap-2 sm:flex-1">
        <label className="text-xs text-gray-600">{t('table.rows_per_page')}:</label>
        <select
          value={pageSize}
          onChange={(e) => onPageSizeChange(Number(e.target.value))}
          className="border border-gray-300 rounded px-2 py-1 text-xs tabular-nums bg-white"
        >
          {pageSizeOptions.map((size) => (
            <option key={size} value={size}>
              {size}
            </option>
          ))}
        </select>
      </div>

      {/* Right: prev/next + page indicator */}
      <div className="flex items-center justify-end gap-1 sm:flex-1">
        <button
          onClick={() => onPageChange(0)}
          disabled={pageIndex === 0}
          className="p-1 text-gray-500 hover:text-gray-700 disabled:opacity-40 disabled:cursor-not-allowed"
          title={t('table.first_page', 'First page')}
        >
          <ChevronsLeft className="h-4 w-4" />
        </button>
        <button
          onClick={() => onPageChange(pageIndex - 1)}
          disabled={pageIndex === 0}
          className="p-1 text-gray-500 hover:text-gray-700 disabled:opacity-40 disabled:cursor-not-allowed"
          title={t('table.prev_page', 'Previous page')}
        >
          <ChevronLeft className="h-4 w-4" />
        </button>

        <span className="text-xs text-gray-700 px-2 tabular-nums whitespace-nowrap">
          {currentPage} / {totalPages || 1}
        </span>

        <button
          onClick={() => onPageChange(pageIndex + 1)}
          disabled={pageIndex >= totalPages - 1}
          className="p-1 text-gray-500 hover:text-gray-700 disabled:opacity-40 disabled:cursor-not-allowed"
          title={t('table.next_page', 'Next page')}
        >
          <ChevronRight className="h-4 w-4" />
        </button>
        <button
          onClick={() => onPageChange(totalPages - 1)}
          disabled={pageIndex >= totalPages - 1}
          className="p-1 text-gray-500 hover:text-gray-700 disabled:opacity-40 disabled:cursor-not-allowed"
          title={t('table.last_page', 'Last page')}
        >
          <ChevronsRight className="h-4 w-4" />
        </button>
      </div>
    </div>
  );
};

export default TableNavigation;
